<template>
  <div class="access-control-tab">
    <div class="loading loading-lg mt-2" v-if="loading"></div>
    <div v-else>
      <div class="stenci-title mt-2 mb-2">Controle de acesso por IP</div>
      <div class="stenci-group stenci-info">
        <div>
          Se você não definir <strong class="text-semibold">nenhum IP</strong>,
          o acesso será permitido de <strong class="text-semibold">qualquer endereço</strong>.
        </div>
        <div class="mt-1">
          Caso adicione <strong class="text-semibold">IPs específicos</strong>,
          o sistema <strong class="text-semibold">bloqueará</strong> automaticamente qualquer
          tentativa de acesso vinda de outros endereços.
        </div>
        <div class="text-info mt-2">
          <strong>Dica:</strong> Certifique-se de incluir seu IP atual para não perder o acesso.
        </div>
      </div>

      <div class="form-group mt-2">
        <div class="columns">
          <div
            class="column col-4 col-md-12 col-sm-12"
            :class="{ 'has-error': $v.newIp.ip.$error }"
          >
            <label class="form-label">Endereço IP</label>
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                :value="newIp.ip"
                @input.stop="onInput"
                placeholder="Ex: 172.217.22.14 ou 2001:db8::1"
                @change="$v.newIp.ip.$touch()"
                @keyup.enter="addIp"
              >
              <span class="input-group-addon text-gray">{{ ipType || 'IP' }}</span>
            </div>
            <div
              class="text-error text-small mt-1"
              v-if="$v.newIp.ip.$dirty && $v.newIp.ip.$error"
            >
              <div v-if="!$v.newIp.ip.required">
                Por favor, informe um endereço IP
              </div>
              <div v-else-if="!$v.newIp.ip.ip">
                Endereço IP inválido.
              </div>
              <div v-else-if="!$v.newIp.ip.uniqueIp">
                Este endereço IP já está na lista
              </div>
            </div>
          </div>
          <div class="column col-6 col-md-12 col-sm-12">
            <label class="form-label">Descrição (opcional)</label>
            <input
              type="text"
              class="form-input"
              v-model="newIp.description"
              placeholder="Ex: Escritório Matriz"
              @keyup.enter="addIp"
            >
          </div>
          <div class="column col-2 col-md-12 col-sm-12">
            <label class="form-label">&nbsp;</label>
            <button
              type="button"
              class="btn btn-primary btn-block"
              @click="addIp"
              :disabled="!canAddIp"
            >Adicionar</button>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <div class="form-group" v-if="localIps.length === 0">
        <div class="empty">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'info-circle']" size="3x" />
          </div>
          <p class="empty-title h5">Nenhum IP cadastrado</p>
          <p class="empty-subtitle">
            Atualmente, o acesso está liberado para qualquer origem.
            <br>
            Para restringir o uso a locais específicos, comece adicionando um endereço de IP.
          </p>
        </div>
      </div>

      <table class="table table-striped" v-else>
        <thead>
          <tr>
            <th>Endereço IP</th>
            <th>Descrição</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(ipItem, index) in localIps" :key="index">
            <td>{{ ipItem.ip }}</td>
            <td>{{ ipItem.description || '-' }}</td>
            <td class="text-right">
              <button
                class="btn btn-sm btn-action btn-error btn-icon"
                @click="removeIp(index)"
                title="Remover"
              >
                <fa-icon :icon="['fal', 'times']" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="form-group mt-2" v-if="isDirty">
        <button
          type="button"
          class="btn btn-primary"
          :class="{ loading: saving }"
          :disabled="saving"
          @click="save"
        >Aplicar</button>
        <button
          type="button"
          class="btn btn-gray-outline ml-2"
          @click="reset"
          :disabled="saving"
        >Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/src/validators';
import ipValidator from '@/data/validators/ip';
import ipaddr from 'ipaddr.js';

export default {
  props: {
    loading: {
      type: Boolean,
    },
    settings: {
      type: Object,
    },
  },
  data() {
    return {
      saving: false,
      localIps: [],
      newIp: {
        ip: '',
        description: '',
      },
      isDirty: false,
    };
  },
  computed: {
    canAddIp() {
      if (!this.$v || !this.$v.newIp || !this.$v.newIp.ip) {
        return false;
      }
      return !this.$v.newIp.ip.$invalid;
    },
    ipType() {
      const ip = this.newIp.ip.replace(/[.:]$/g, '');
      if (!ip || ip.length < 3 || !ipaddr.isValid(ip)) return null;
      return ipaddr.parse(ip).kind() === 'ipv4' ? 'IPv4' : 'IPv6';
    },
  },
  validations() {
    const uniqueIp = (value) => {
      if (!value || typeof value !== 'string' || value.trim() === '') {
        return true;
      }

      const ip = value.trim();
      return !this.localIps.some(ipItem => ipItem.ip === ip);
    };
    return {
      newIp: {
        ip: {
          required,
          ip: ipValidator,
          uniqueIp,
        },
      },
    };
  },
  watch: {
    loading() {
      this.updateLocalIps();
    },
  },
  mounted() {
    this.updateLocalIps();
  },
  methods: {
    sanitizeIP(value) {
      return value.replace(/[^0-9a-fA-F:.]/g, '');
    },
    onInput(e) {
      const sanitized = this.sanitizeIP(e.target.value);
      e.target.value = sanitized;
      this.newIp.ip = sanitized;
    },
    addIp() {
      this.$v.newIp.ip.$touch();
      if (this.$v.newIp.ip.$error) {
        return;
      }

      this.localIps.push({
        ip: this.newIp.ip.trim(),
        description: this.newIp.description.trim(),
      });

      this.newIp = {
        ip: '',
        description: '',
      };

      this.isDirty = true;
      this.$v.newIp.$reset();
    },
    removeIp(index) {
      this.localIps.splice(index, 1);
      this.isDirty = true;
    },
    async save() {
      this.saving = true;
      try {
        await this.$http.put('/facility-settings', {
          accessControl: {
            ips: this.localIps,
          },
        });
        this.$toast.success('Configuração de IP salva com sucesso');
        this.isDirty = false;
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    reset() {
      this.updateLocalIps();
      this.newIp = {
        ip: '',
        description: '',
      };
      this.$v.$reset();
    },
    updateLocalIps() {
      const accessControl = this.settings ? this.settings.accessControl : null;
      this.localIps = accessControl && Array.isArray(accessControl.ips)
        ? [...accessControl.ips]
        : [];
      this.isDirty = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./src/assets/scss/_variables.scss";

.access-control-tab {
  .divider {
    margin: $layout-spacing 0;
  }

  .empty {
    padding: $layout-spacing-lg;
    text-align: center;

    .empty-icon {
      color: $gray-color;
      margin-bottom: $layout-spacing;
    }
  }

  .table {
    margin-top: $layout-spacing;
  }
}
</style>
